import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'next-i18next';
import AutoColumnsGrid from '../../../new-components/display-system/AutoColumnsGrid';
import { EntityOffer, PaymentInterface } from '../../../new-lib/types/entity';
import OfferCardMobile from '../../offers/components/cards/OfferCardMobile';
import OfferCardDesktop from '../../offers/components/cards/OfferCardDesktop';

type PaymentDetails = {
  payment_percentage: string;
  payment_type: string;
  payment_duration: string;
};

export type OfferCardProps = {
  offer: EntityOffer;
  entity: any;
  paymentDetails: PaymentDetails;
  originalPaymentPlan: PaymentInterface;
};

export type OfferCardBadgeProps = {
  offerName: string;
};

interface OfferCardContainerProps {
  offers: EntityOffer[];
  entity: any;
}

const OfferCardsContainer = ({ offers, entity }: OfferCardContainerProps) => {
  const isMobile = useSelector((state: any) => state.app.isMobile);

  const { t } = useTranslation(['entity', 'common']);

  const getPaymentDetails = (offer: EntityOffer) => {
    if (offer?.discount) {
      const paymentDetails = {
        payment_percentage: `${Math.round(offer.discount)}%`,
        payment_type: '',
        payment_duration: t('discount')
      };

      return paymentDetails;
    }
    if (offer.offer?.cash_discount?.percent) {
      const paymentDetails = {
        payment_percentage: `${Math.round(
          offer.offer?.cash_discount.percent
        )}%`,
        payment_type: '',
        payment_duration: t('cash_discount')
      };

      return paymentDetails;
    }

    if (offer.offer?.down_payment?.percent !== null) {
      const paymentDetails = {
        payment_percentage: `${offer.offer?.down_payment?.percent}%`,
        payment_type: t('down_payment'),
        payment_duration: `${offer.offer?.years} ${t('years', {
          ns: 'common'
        })}`
      };

      return paymentDetails;
    }

    return { payment_percentage: '', payment_type: '', payment_duration: '' };
  };

  return (
    <Container>
      <h2>{t('offers', { ns: 'common' })}</h2>

      {isMobile ? (
        <OfferCardSlider>
          {offers.map((offer, index) => {
            return (
              <OfferCardMobile
                key={`${offer?.offer?.id}${index}`}
                offer={offer}
                entity={entity}
                paymentDetails={getPaymentDetails(offer)}
                originalPaymentPlan={offer?.original_payment_plan}
              />
            );
          })}
        </OfferCardSlider>
      ) : (
        <AutoColumnsGrid
          minWidth={'280px'}
          rowsGap={'16px'}
          columnsGap={'16px'}
        >
          {offers.map((offer, index) => {
            return (
              <OfferCardDesktop
                key={`${offer?.offer?.id}${index}`}
                offer={offer}
                entity={entity}
                paymentDetails={getPaymentDetails(offer)}
                originalPaymentPlan={offer?.original_payment_plan}
              />
            );
          })}
        </AutoColumnsGrid>
      )}
    </Container>
  );
};

const Container = styled.div`
  h2 {
    font-family: materialBold;
    font-size: 24px;
    margin-bottom: 20px;
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.tablet.min}) {
    h2 {
      font-size: 16px;
    }
  }
`;

const OfferCardSlider = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-gap: 10px;
  overflow-x: scroll;
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`;

export default OfferCardsContainer;
